import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Star, LogOut, Upload, Plus, Users, BarChart3, 
  Chrome, Facebook, MapPin, Trash2, Edit, Check, X,
  FileText, AlertCircle, Mail, Shield, Instagram, Linkedin, Twitter, Youtube, Music,
  Home, BedDouble, Search, ChevronLeft, ChevronRight, Filter, UserX, UserCheck, Key, Eye
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEBSITE_URL = process.env.REACT_APP_WEBSITE_URL || "https://www.reviewmybusiness.ie";

const platformIcons = {
  chrome: Chrome,
  facebook: Facebook,
  'map-pin': MapPin,
  star: Star,
  instagram: Instagram,
  linkedin: Linkedin,
  twitter: Twitter,
  youtube: Youtube,
  music: Music,
  home: Home,
  'bed-double': BedDouble,
};

const AdminDashboard = () => {
  const { user, logout, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [stats, setStats] = useState(null);
  const [plaques, setPlaques] = useState([]);
  const [plaquePagination, setPlaquePagination] = useState({ total: 0, page: 1, per_page: 25, total_pages: 0 });
  const [plaqueSearch, setPlaqueSearch] = useState('');
  const [plaqueFilterPlatform, setPlaqueFilterPlatform] = useState('all');
  const [plaqueFilterStatus, setPlaqueFilterStatus] = useState('all');
  const [plaqueFilterType, setPlaqueFilterType] = useState('all');
  const [plaqueFilterUser, setPlaqueFilterUser] = useState('all');
  const [selectedPlaques, setSelectedPlaques] = useState(new Set());
  const [editingPlaque, setEditingPlaque] = useState(null);
  const [plaqueDialogOpen, setPlaqueDialogOpen] = useState(false);
  const [plaqueForm, setPlaqueForm] = useState({
    business_name: '', review_url: '', redirect_url: '', threshold_stars: 4, is_registered: false
  });
  const [savingPlaque, setSavingPlaque] = useState(false);
  const [deletingPlaques, setDeletingPlaques] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Platform form
  const [platformDialogOpen, setPlatformDialogOpen] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [platformForm, setPlatformForm] = useState({
    prefix: '',
    name: '',
    icon: 'star'
  });
  
  // User form
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    businessName: '',
    isAdmin: false
  });
  const [savingUser, setSavingUser] = useState(false);
  
  // User search/filter
  const [userSearch, setUserSearch] = useState('');
  const [userFilterStatus, setUserFilterStatus] = useState('all');
  const [userFilterRole, setUserFilterRole] = useState('all');
  
  // Set password dialog
  const [setPasswordOpen, setSetPasswordOpen] = useState(false);
  const [setPasswordUser, setSetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchPlaques = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, per_page: 25 });
      if (plaqueSearch) params.set('search', plaqueSearch);
      if (plaqueFilterPlatform !== 'all') params.set('platform', plaqueFilterPlatform);
      if (plaqueFilterStatus !== 'all') params.set('status', plaqueFilterStatus);
      if (plaqueFilterType !== 'all') params.set('plaque_type', plaqueFilterType);
      if (plaqueFilterUser !== 'all') params.set('user_id', plaqueFilterUser);

      const res = await axios.get(`${API}/admin/plaques?${params}`);
      setPlaques(res.data.plaques);
      setPlaquePagination({
        total: res.data.total,
        page: res.data.page,
        per_page: res.data.per_page,
        total_pages: res.data.total_pages
      });
      setSelectedPlaques(new Set());
    } catch (error) {
      toast.error('Failed to load plaques');
    }
  }, [plaqueSearch, plaqueFilterPlatform, plaqueFilterStatus, plaqueFilterType, plaqueFilterUser]);

  useEffect(() => {
    if (!loading) {
      fetchPlaques(1);
    }
  }, [plaqueSearch, plaqueFilterPlatform, plaqueFilterStatus, plaqueFilterType, plaqueFilterUser, loading, fetchPlaques]);

  const fetchData = async () => {
    try {
      const [statsRes, plaquesRes, platformsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/plaques?page=1&per_page=25`),
        axios.get(`${API}/admin/platforms`),
        axios.get(`${API}/admin/users`)
      ]);
      
      setStats(statsRes.data);
      setPlaques(plaquesRes.data.plaques);
      setPlaquePagination({
        total: plaquesRes.data.total,
        page: plaquesRes.data.page,
        per_page: plaquesRes.data.per_page,
        total_pages: plaquesRes.data.total_pages
      });
      setPlatforms(platformsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = WEBSITE_URL;
    toast.success('Logged out successfully');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/admin/codes/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(response.data);
      toast.success(`Imported ${response.data.imported} codes`);
      fetchPlaques(1);
      const statsRes = await axios.get(`${API}/admin/stats`);
      setStats(statsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handlePlatformSubmit = async () => {
    try {
      if (editingPlatform) {
        await axios.put(`${API}/admin/platforms/${editingPlatform.id}`, {
          name: platformForm.name,
          icon: platformForm.icon
        });
        toast.success('Platform updated');
      } else {
        await axios.post(`${API}/admin/platforms`, platformForm);
        toast.success('Platform created');
      }
      
      setPlatformDialogOpen(false);
      setEditingPlatform(null);
      setPlatformForm({ prefix: '', name: '', icon: 'star' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save platform');
    }
  };

  const handleDeletePlatform = async (platformId) => {
    if (!window.confirm('Are you sure you want to delete this platform?')) return;
    
    try {
      await axios.delete(`${API}/admin/platforms/${platformId}`);
      toast.success('Platform deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete platform');
    }
  };

  const openEditPlatform = (platform) => {
    setEditingPlatform(platform);
    setPlatformForm({
      prefix: platform.prefix,
      name: platform.name,
      icon: platform.icon
    });
    setPlatformDialogOpen(true);
  };

  // User management functions
  const handleUserSubmit = async () => {
    setSavingUser(true);
    try {
      if (editingUser) {
        await axios.put(`${API}/admin/users/${editingUser.id}`, {
          email: userForm.email,
          business_name: userForm.businessName,
          is_admin: userForm.isAdmin
        });
        toast.success('User updated');
      } else {
        if (!userForm.password) {
          toast.error('Password is required for new users');
          setSavingUser(false);
          return;
        }
        await axios.post(`${API}/admin/users`, {
          email: userForm.email,
          password: userForm.password,
          business_name: userForm.businessName,
          is_admin: userForm.isAdmin
        });
        toast.success('User created');
      }
      
      setUserDialogOpen(false);
      setEditingUser(null);
      setUserForm({ email: '', password: '', businessName: '', isAdmin: false });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setSavingUser(false);
    }
  };

  const openEditUser = (targetUser) => {
    setEditingUser(targetUser);
    setUserForm({
      email: targetUser.email,
      password: '',
      businessName: targetUser.business_name,
      isAdmin: targetUser.is_admin
    });
    setUserDialogOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? Their plaques will be unregistered.')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
      const statsRes = await axios.get(`${API}/admin/stats`);
      setStats(statsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleSendPasswordReset = async (userId, email) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/reset-password`);
      toast.success(`Password reset email sent to ${email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (userSearch) params.set('search', userSearch);
      if (userFilterStatus !== 'all') params.set('status', userFilterStatus);
      if (userFilterRole !== 'all') params.set('role', userFilterRole);
      const res = await axios.get(`${API}/admin/users?${params}`);
      setUsers(res.data);
    } catch (error) {
      toast.error('Failed to load users');
    }
  }, [userSearch, userFilterStatus, userFilterRole]);

  useEffect(() => {
    if (!loading) {
      fetchUsers();
    }
  }, [userSearch, userFilterStatus, userFilterRole, loading, fetchUsers]);

  const handleToggleUserActive = async (userId, currentlyActive) => {
    const action = currentlyActive ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this user?${currentlyActive ? ' Their plaques will also be disabled.' : ''}`)) return;
    
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/toggle-active`);
      toast.success(res.data.message);
      fetchUsers();
      // Refresh stats
      const statsRes = await axios.get(`${API}/admin/stats`);
      setStats(statsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  const handleSetPassword = async () => {
    if (!setPasswordUser || !newPassword) return;
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSettingPassword(true);
    try {
      await axios.post(`${API}/admin/users/${setPasswordUser.id}/set-password`, {
        new_password: newPassword
      });
      toast.success(`Password updated for ${setPasswordUser.email}`);
      setSetPasswordOpen(false);
      setSetPasswordUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setSettingPassword(false);
    }
  };

  const handleImpersonate = async (targetUser) => {
    if (!window.confirm(`Log in as ${targetUser.email}? You'll see their dashboard and can return to admin at any time.`)) return;
    try {
      const res = await axios.post(`${API}/admin/users/${targetUser.id}/impersonate`);
      startImpersonation(res.data.access_token, res.data.user);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate user');
    }
  };

  // Plaque management functions
  const openEditPlaque = (plaque) => {
    setEditingPlaque(plaque);
    setPlaqueForm({
      business_name: plaque.business_name || '',
      review_url: plaque.review_url || '',
      redirect_url: plaque.redirect_url || '',
      threshold_stars: plaque.threshold_stars || 4,
      is_registered: plaque.is_registered
    });
    setPlaqueDialogOpen(true);
  };

  const handlePlaqueEditSubmit = async () => {
    if (!editingPlaque) return;
    setSavingPlaque(true);
    try {
      const payload = {};
      if (plaqueForm.business_name !== (editingPlaque.business_name || ''))
        payload.business_name = plaqueForm.business_name;
      if (plaqueForm.review_url !== (editingPlaque.review_url || ''))
        payload.review_url = plaqueForm.review_url;
      if (plaqueForm.redirect_url !== (editingPlaque.redirect_url || ''))
        payload.redirect_url = plaqueForm.redirect_url;
      if (plaqueForm.threshold_stars !== editingPlaque.threshold_stars)
        payload.threshold_stars = plaqueForm.threshold_stars;
      if (plaqueForm.is_registered !== editingPlaque.is_registered)
        payload.is_registered = plaqueForm.is_registered;

      await axios.put(`${API}/admin/plaques/${editingPlaque.id}`, payload);
      toast.success('Plaque updated');
      setPlaqueDialogOpen(false);
      setEditingPlaque(null);
      fetchPlaques(plaquePagination.page);
      // Refresh stats too
      const statsRes = await axios.get(`${API}/admin/stats`);
      setStats(statsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update plaque');
    } finally {
      setSavingPlaque(false);
    }
  };

  const toggleSelectPlaque = (id) => {
    setSelectedPlaques(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedPlaques.size === plaques.length) {
      setSelectedPlaques(new Set());
    } else {
      setSelectedPlaques(new Set(plaques.map(p => p.id)));
    }
  };

  const handleBatchDelete = async () => {
    if (selectedPlaques.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedPlaques.size} plaque code(s)? This cannot be undone.`)) return;

    setDeletingPlaques(true);
    try {
      const res = await axios.post(`${API}/admin/plaques/batch-delete`, {
        ids: Array.from(selectedPlaques)
      });
      toast.success(`Deleted ${res.data.deleted} plaque code(s)`);
      setSelectedPlaques(new Set());
      fetchPlaques(plaquePagination.page);
      // Refresh stats
      const statsRes = await axios.get(`${API}/admin/stats`);
      setStats(statsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete plaques');
    } finally {
      setDeletingPlaques(false);
    }
  };

  const getPlatformIcon = (iconName) => {
    return platformIcons[iconName] || Star;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-subtle)]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href={WEBSITE_URL} className="flex items-center gap-2">
              <img src="/logo.png" alt="ReviewMyBusiness.ie" className="h-10" />
            </a>
            <Badge className="bg-amber-100 text-amber-700">Admin</Badge>
          </div>
          
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="outline" size="sm" data-testid="user-dashboard-link">
                User Dashboard
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="font-dashboard-heading text-2xl sm:text-3xl font-bold tracking-tight">
            Admin Dashboard
          </h1>
          <p className="text-[var(--color-text-secondary)] mt-1">
            Manage plaques, platforms, users, and import codes
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[var(--color-text-muted)] uppercase tracking-wide font-medium">
                      Total Codes
                    </p>
                    <p className="text-3xl font-dashboard-heading font-bold mt-1" data-testid="admin-total-codes">
                      {stats.total_codes}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-[var(--color-accent)]" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[var(--color-text-muted)] uppercase tracking-wide font-medium">
                      Registered
                    </p>
                    <p className="text-3xl font-dashboard-heading font-bold mt-1 text-green-600" data-testid="admin-registered">
                      {stats.registered_codes}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                    <Check className="w-6 h-6 text-green-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[var(--color-text-muted)] uppercase tracking-wide font-medium">
                      Total Users
                    </p>
                    <p className="text-3xl font-dashboard-heading font-bold mt-1" data-testid="admin-total-users">
                      {stats.total_users}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-purple-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[var(--color-text-muted)] uppercase tracking-wide font-medium">
                      Avg Rating
                    </p>
                    <p className="text-3xl font-dashboard-heading font-bold mt-1" data-testid="admin-avg-rating">
                      {stats.average_rating > 0 ? stats.average_rating : '—'}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-yellow-50 rounded-xl flex items-center justify-center">
                    <Star className="w-6 h-6 text-yellow-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="plaques" className="space-y-6">
          <TabsList>
            <TabsTrigger value="plaques" data-testid="plaques-tab">All Plaques</TabsTrigger>
            <TabsTrigger value="users" data-testid="users-tab">Users</TabsTrigger>
            <TabsTrigger value="platforms" data-testid="platforms-tab">Platforms</TabsTrigger>
            <TabsTrigger value="import" data-testid="import-tab">Import Codes</TabsTrigger>
          </TabsList>

          {/* Plaques Tab */}
          <TabsContent value="plaques">
            <Card className="border-0 shadow-sm">
              <CardHeader className="border-b border-gray-100">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="font-dashboard-heading text-lg font-semibold">
                      All Plaque Codes ({plaquePagination.total})
                    </CardTitle>
                    <CardDescription>Search, filter, edit, and manage all plaque codes</CardDescription>
                  </div>
                  {selectedPlaques.size > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleBatchDelete}
                      disabled={deletingPlaques}
                      data-testid="batch-delete-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {deletingPlaques ? 'Deleting...' : `Delete ${selectedPlaques.size} selected`}
                    </Button>
                  )}
                </div>
                {/* Search & Filters */}
                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search by code or business name..."
                      value={plaqueSearch}
                      onChange={(e) => setPlaqueSearch(e.target.value)}
                      className="pl-9"
                      data-testid="plaque-search-input"
                    />
                  </div>
                  <Select value={plaqueFilterPlatform} onValueChange={setPlaqueFilterPlatform}>
                    <SelectTrigger className="w-full sm:w-[180px]" data-testid="plaque-filter-platform">
                      <SelectValue placeholder="All Platforms" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Platforms</SelectItem>
                      {platforms.map(p => (
                        <SelectItem key={p.id} value={p.prefix}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={plaqueFilterStatus} onValueChange={setPlaqueFilterStatus}>
                    <SelectTrigger className="w-full sm:w-[160px]" data-testid="plaque-filter-status">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="registered">Registered</SelectItem>
                      <SelectItem value="available">Available</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={plaqueFilterType} onValueChange={setPlaqueFilterType}>
                    <SelectTrigger className="w-full sm:w-[140px]" data-testid="plaque-filter-type">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="review">Review</SelectItem>
                      <SelectItem value="follow">Follow</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={plaqueFilterUser} onValueChange={setPlaqueFilterUser}>
                    <SelectTrigger className="w-full sm:w-[200px]" data-testid="plaque-filter-user">
                      <SelectValue placeholder="All Users" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Users</SelectItem>
                      {users.filter(u => !u.is_admin).map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {plaques.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-[var(--color-text-muted)]">
                      {plaqueSearch || plaqueFilterPlatform !== 'all' || plaqueFilterStatus !== 'all' || plaqueFilterType !== 'all' || plaqueFilterUser !== 'all'
                        ? 'No plaques match your filters.'
                        : 'No plaque codes yet. Import some codes to get started.'}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th className="w-10">
                              <Checkbox
                                checked={selectedPlaques.size === plaques.length && plaques.length > 0}
                                onCheckedChange={toggleSelectAll}
                                data-testid="select-all-plaques"
                              />
                            </th>
                            <th>Code</th>
                            <th>Type</th>
                            <th>Platform</th>
                            <th>Business</th>
                            <th>Owner</th>
                            <th>Status</th>
                            <th>Created</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {plaques.map((plaque) => {
                            const PlatformIcon = getPlatformIcon(plaque.platform_icon);
                            return (
                              <tr key={plaque.id} data-testid={`admin-plaque-${plaque.code}`}>
                                <td>
                                  <Checkbox
                                    checked={selectedPlaques.has(plaque.id)}
                                    onCheckedChange={() => toggleSelectPlaque(plaque.id)}
                                    data-testid={`select-plaque-${plaque.code}`}
                                  />
                                </td>
                                <td>
                                  <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                                    {plaque.code}
                                  </code>
                                </td>
                                <td>
                                  <Badge className={plaque.type === 'follow' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                                    {plaque.type === 'follow' ? 'Follow' : 'Review'}
                                  </Badge>
                                </td>
                                <td>
                                  <div className="flex items-center gap-2">
                                    <PlatformIcon className="w-4 h-4 text-[var(--color-text-secondary)]" />
                                    <span className="text-sm">{plaque.platform_name}</span>
                                  </div>
                                </td>
                                <td className="text-sm">{plaque.business_name || '—'}</td>
                                <td className="text-sm">
                                  {plaque.user_email ? (
                                    <span className="text-xs" title={plaque.user_email}>{plaque.user_email}</span>
                                  ) : '—'}
                                </td>
                                <td>
                                  <Badge className={plaque.is_registered ? 'badge-active' : 'badge-inactive'}>
                                    {plaque.is_registered ? 'Registered' : 'Available'}
                                  </Badge>
                                </td>
                                <td className="text-[var(--color-text-muted)] text-sm">
                                  {new Date(plaque.created_at).toLocaleDateString()}
                                </td>
                                <td>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openEditPlaque(plaque)}
                                    data-testid={`edit-plaque-${plaque.code}`}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    {plaquePagination.total_pages > 1 && (
                      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                        <p className="text-sm text-[var(--color-text-muted)]">
                          Showing {((plaquePagination.page - 1) * plaquePagination.per_page) + 1}–{Math.min(plaquePagination.page * plaquePagination.per_page, plaquePagination.total)} of {plaquePagination.total}
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={plaquePagination.page <= 1}
                            onClick={() => fetchPlaques(plaquePagination.page - 1)}
                            data-testid="plaques-prev-page"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          <span className="text-sm font-medium px-2">
                            Page {plaquePagination.page} of {plaquePagination.total_pages}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={plaquePagination.page >= plaquePagination.total_pages}
                            onClick={() => fetchPlaques(plaquePagination.page + 1)}
                            data-testid="plaques-next-page"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Edit Plaque Dialog */}
            <Dialog open={plaqueDialogOpen} onOpenChange={setPlaqueDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    Edit Plaque — {editingPlaque?.code}
                  </DialogTitle>
                  <DialogDescription>
                    Update plaque details, URLs, and registration status
                  </DialogDescription>
                </DialogHeader>
                {editingPlaque && (
                  <div className="space-y-4 pt-4">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Badge variant="outline">{editingPlaque.platform_name}</Badge>
                      <Badge className={editingPlaque.platform_type === 'follow' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                        {editingPlaque.platform_type}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="plaqueBusiness">Business Name</Label>
                      <Input
                        id="plaqueBusiness"
                        value={plaqueForm.business_name}
                        onChange={(e) => setPlaqueForm({ ...plaqueForm, business_name: e.target.value })}
                        placeholder="Business name"
                        data-testid="edit-plaque-business"
                      />
                    </div>
                    {editingPlaque.platform_type === 'rating' && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="plaqueReviewUrl">Review URL</Label>
                          <Input
                            id="plaqueReviewUrl"
                            value={plaqueForm.review_url}
                            onChange={(e) => setPlaqueForm({ ...plaqueForm, review_url: e.target.value })}
                            placeholder="https://..."
                            data-testid="edit-plaque-review-url"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="plaqueThreshold">Rating Threshold (redirect at or above)</Label>
                          <Select
                            value={String(plaqueForm.threshold_stars)}
                            onValueChange={(v) => setPlaqueForm({ ...plaqueForm, threshold_stars: parseInt(v) })}
                          >
                            <SelectTrigger data-testid="edit-plaque-threshold">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[1,2,3,4,5].map(n => (
                                <SelectItem key={n} value={String(n)}>{n} star{n > 1 ? 's' : ''}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}
                    {editingPlaque.platform_type === 'follow' && (
                      <div className="space-y-2">
                        <Label htmlFor="plaqueRedirectUrl">Redirect URL</Label>
                        <Input
                          id="plaqueRedirectUrl"
                          value={plaqueForm.redirect_url}
                          onChange={(e) => setPlaqueForm({ ...plaqueForm, redirect_url: e.target.value })}
                          placeholder="https://..."
                          data-testid="edit-plaque-redirect-url"
                        />
                      </div>
                    )}
                    {editingPlaque.is_registered && (
                      <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                        <div>
                          <Label>Unregister Plaque</Label>
                          <p className="text-xs text-[var(--color-text-muted)]">
                            This will clear the owner and all registration data
                          </p>
                        </div>
                        <Switch
                          checked={!plaqueForm.is_registered}
                          onCheckedChange={(checked) => setPlaqueForm({ ...plaqueForm, is_registered: !checked })}
                          data-testid="edit-plaque-unregister"
                        />
                      </div>
                    )}
                    {editingPlaque.user_email && (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-[var(--color-text-muted)] mb-1">Registered by</p>
                        <p className="text-sm font-medium">{editingPlaque.user_email}</p>
                      </div>
                    )}
                    <Button
                      onClick={handlePlaqueEditSubmit}
                      className="w-full"
                      disabled={savingPlaque}
                      data-testid="save-plaque-btn"
                    >
                      {savingPlaque ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="border-0 shadow-sm">
              <CardHeader className="border-b border-gray-100">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="font-dashboard-heading text-lg font-semibold">
                      User Accounts ({users.length})
                    </CardTitle>
                    <CardDescription>Manage customer and admin accounts</CardDescription>
                  </div>
                  <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        onClick={() => {
                          setEditingUser(null);
                          setUserForm({ email: '', password: '', businessName: '', isAdmin: false });
                        }}
                        data-testid="add-user-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add User
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>
                          {editingUser ? 'Edit User' : 'Create New User'}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div className="space-y-2">
                          <Label htmlFor="userEmail">Email</Label>
                          <Input
                            id="userEmail"
                            type="email"
                            value={userForm.email}
                            onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                            placeholder="user@example.com"
                            data-testid="user-email-input"
                          />
                        </div>
                        {!editingUser && (
                          <div className="space-y-2">
                            <Label htmlFor="userPassword">Password</Label>
                            <Input
                              id="userPassword"
                              type="password"
                              value={userForm.password}
                              onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                              placeholder="••••••••"
                              data-testid="user-password-input"
                            />
                          </div>
                        )}
                        <div className="space-y-2">
                          <Label htmlFor="userBusinessName">Business Name</Label>
                          <Input
                            id="userBusinessName"
                            value={userForm.businessName}
                            onChange={(e) => setUserForm({ ...userForm, businessName: e.target.value })}
                            placeholder="Business Name"
                            data-testid="user-business-input"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label htmlFor="userIsAdmin">Admin Access</Label>
                            <p className="text-xs text-[var(--color-text-muted)]">
                              Grant admin privileges to this user
                            </p>
                          </div>
                          <Switch
                            id="userIsAdmin"
                            checked={userForm.isAdmin}
                            onCheckedChange={(checked) => setUserForm({ ...userForm, isAdmin: checked })}
                            data-testid="user-admin-switch"
                          />
                        </div>
                        <Button 
                          onClick={handleUserSubmit}
                          className="w-full"
                          disabled={savingUser}
                          data-testid="save-user-btn"
                        >
                          {savingUser ? 'Saving...' : editingUser ? 'Update User' : 'Create User'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                {/* Search & Filters */}
                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search by email or business name..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="pl-9"
                      data-testid="user-search-input"
                    />
                  </div>
                  <Select value={userFilterStatus} onValueChange={setUserFilterStatus}>
                    <SelectTrigger className="w-full sm:w-[160px]" data-testid="user-filter-status">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={userFilterRole} onValueChange={setUserFilterRole}>
                    <SelectTrigger className="w-full sm:w-[160px]" data-testid="user-filter-role">
                      <SelectValue placeholder="All Roles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      <SelectItem value="admin">Admins</SelectItem>
                      <SelectItem value="customer">Customers</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {users.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-[var(--color-text-muted)]">
                      {userSearch || userFilterStatus !== 'all' || userFilterRole !== 'all'
                        ? 'No users match your filters.'
                        : 'No users yet.'}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Business</th>
                          <th>Plaques</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u.id} data-testid={`user-row-${u.id}`} className={u.is_active === false ? 'opacity-60' : ''}>
                            <td>
                              <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${u.is_active === false ? 'bg-red-100' : 'bg-gray-100'}`}>
                                  <span className="text-xs font-medium">
                                    {u.email.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <span className="text-sm">{u.email}</span>
                              </div>
                            </td>
                            <td>{u.business_name}</td>
                            <td>
                              <Badge variant="outline">{u.plaque_count}</Badge>
                            </td>
                            <td>
                              {u.is_admin ? (
                                <Badge className="bg-amber-100 text-amber-700">
                                  <Shield className="w-3 h-3 mr-1" />
                                  Admin
                                </Badge>
                              ) : (
                                <Badge variant="outline">Customer</Badge>
                              )}
                            </td>
                            <td>
                              {u.is_active === false ? (
                                <Badge className="bg-red-100 text-red-700" data-testid={`user-status-${u.id}`}>Inactive</Badge>
                              ) : (
                                <Badge className="badge-active" data-testid={`user-status-${u.id}`}>Active</Badge>
                              )}
                            </td>
                            <td className="text-[var(--color-text-muted)]">
                              {new Date(u.created_at).toLocaleDateString()}
                            </td>
                            <td>
                              <div className="flex items-center gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => openEditUser(u)}
                                  data-testid={`edit-user-${u.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                {u.id !== user?.id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleToggleUserActive(u.id, u.is_active !== false)}
                                    title={u.is_active === false ? 'Activate user' : 'Deactivate user'}
                                    className={u.is_active === false ? 'text-green-600 hover:text-green-700 hover:bg-green-50' : 'text-orange-500 hover:text-orange-600 hover:bg-orange-50'}
                                    data-testid={`toggle-active-${u.id}`}
                                  >
                                    {u.is_active === false ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                                  </Button>
                                )}
                                {!u.is_admin && u.is_active !== false && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleImpersonate(u)}
                                    title="Log in as this user"
                                    className="text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                                    data-testid={`impersonate-${u.id}`}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => {
                                    setSetPasswordUser(u);
                                    setNewPassword('');
                                    setSetPasswordOpen(true);
                                  }}
                                  title="Set password"
                                  data-testid={`set-password-${u.id}`}
                                >
                                  <Key className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleSendPasswordReset(u.id, u.email)}
                                  title="Send password reset email"
                                  data-testid={`reset-user-${u.id}`}
                                >
                                  <Mail className="w-4 h-4" />
                                </Button>
                                {u.id !== user?.id && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => handleDeleteUser(u.id)}
                                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                    data-testid={`delete-user-${u.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Set Password Dialog */}
          <Dialog open={setPasswordOpen} onOpenChange={setSetPasswordOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Set Password</DialogTitle>
                <DialogDescription>
                  Set a new password for {setPasswordUser?.email}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimum 6 characters"
                    data-testid="set-password-input"
                  />
                </div>
                <Button
                  onClick={handleSetPassword}
                  className="w-full"
                  disabled={settingPassword || newPassword.length < 6}
                  data-testid="set-password-submit"
                >
                  {settingPassword ? 'Setting...' : 'Set Password'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Platforms Tab */}
          <TabsContent value="platforms">
            <Card className="border-0 shadow-sm">
              <CardHeader className="border-b border-gray-100 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-dashboard-heading text-lg font-semibold">
                    Platforms
                  </CardTitle>
                  <CardDescription>Manage the available platforms</CardDescription>
                </div>
                <Dialog open={platformDialogOpen} onOpenChange={setPlatformDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      onClick={() => {
                        setEditingPlatform(null);
                        setPlatformForm({ prefix: '', name: '', icon: 'star' });
                      }}
                      data-testid="add-platform-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Platform
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>
                        {editingPlatform ? 'Edit Platform' : 'Add New Platform'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label htmlFor="prefix">Prefix (Single Letter)</Label>
                        <Input
                          id="prefix"
                          value={platformForm.prefix}
                          onChange={(e) => setPlatformForm({ ...platformForm, prefix: e.target.value.toUpperCase().slice(0, 1) })}
                          placeholder="G"
                          maxLength={1}
                          disabled={!!editingPlatform}
                          data-testid="platform-prefix-input"
                        />
                        <p className="text-xs text-[var(--color-text-muted)]">
                          This letter will prefix all codes for this platform
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="name">Platform Name</Label>
                        <Input
                          id="name"
                          value={platformForm.name}
                          onChange={(e) => setPlatformForm({ ...platformForm, name: e.target.value })}
                          placeholder="Google Reviews"
                          data-testid="platform-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="icon">Icon</Label>
                        <div className="flex flex-wrap gap-2">
                          {['star', 'chrome', 'facebook', 'map-pin', 'home', 'bed-double', 'instagram', 'linkedin', 'twitter', 'youtube', 'music'].map((icon) => {
                            const IconComponent = platformIcons[icon];
                            return (
                              <button
                                key={icon}
                                type="button"
                                onClick={() => setPlatformForm({ ...platformForm, icon })}
                                className={`p-3 rounded-lg border-2 transition-colors ${
                                  platformForm.icon === icon 
                                    ? 'border-[var(--color-accent)] bg-blue-50' 
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                                data-testid={`icon-${icon}`}
                              >
                                <IconComponent className="w-5 h-5" />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <Button 
                        onClick={handlePlatformSubmit}
                        className="w-full"
                        data-testid="save-platform-btn"
                      >
                        {editingPlatform ? 'Update Platform' : 'Create Platform'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="p-0">
                {platforms.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-[var(--color-text-muted)]">No platforms configured yet.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {platforms.map((platform) => {
                      const PlatformIcon = getPlatformIcon(platform.icon);
                      return (
                        <div 
                          key={platform.id} 
                          className="p-4 flex items-center justify-between"
                          data-testid={`platform-${platform.prefix}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                              <PlatformIcon className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{platform.name}</span>
                                <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">
                                  {platform.prefix}
                                </code>
                                <Badge variant="outline" className="text-xs">
                                  {platform.platform_type || 'rating'}
                                </Badge>
                              </div>
                              <p className="text-sm text-[var(--color-text-muted)]">
                                {plaques.filter(p => p.prefix === platform.prefix).length} codes
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openEditPlatform(platform)}
                              data-testid={`edit-platform-${platform.prefix}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeletePlatform(platform.id)}
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              data-testid={`delete-platform-${platform.prefix}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Import Tab */}
          <TabsContent value="import">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="font-dashboard-heading text-lg font-semibold">
                  Import Plaque Codes
                </CardTitle>
                <CardDescription>
                  Upload a CSV file with plaque codes. The file should have a header row with "platform", "code", and "type" columns.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="csv-upload"
                    data-testid="csv-upload-input"
                  />
                  <label 
                    htmlFor="csv-upload"
                    className="cursor-pointer"
                  >
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Upload className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="font-medium mb-1">
                      {importing ? 'Importing...' : 'Click to upload CSV'}
                    </p>
                    <p className="text-sm text-[var(--color-text-muted)]">
                      CSV file with "platform", "code", and "type" columns
                    </p>
                  </label>
                </div>

                {/* Import Result */}
                {importResult && (
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-2">Import Results</h4>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>{importResult.imported} imported</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <X className="w-4 h-4 text-red-500" />
                        <span>{importResult.skipped} skipped</span>
                      </div>
                    </div>
                    {importResult.errors.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-red-600 mb-2">Errors:</p>
                        <ul className="text-sm text-[var(--color-text-secondary)] space-y-1">
                          {importResult.errors.map((error, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                              {error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* CSV Format Help */}
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    CSV Format
                  </h4>
                  <p className="text-sm text-[var(--color-text-secondary)] mb-2">
                    Your CSV file should have a header row with "platform", "code", and "type" columns:
                  </p>
                  <pre className="bg-white p-3 rounded text-sm font-mono overflow-x-auto">
{`platform,code,type
G,87XVH89VV,review
G,12ABC34DE,follow
F,98ZYX76WV,review
T,11QRS22TU,review
I,55INSTA01,follow
L,77LINKED2,follow`}
                  </pre>
                  <p className="text-xs text-[var(--color-text-muted)] mt-2">
                    Platform codes: G=Google, F=Facebook, T=TripAdvisor, A=Airbnb, B=Booking.com, I=Instagram, L=LinkedIn, X=Twitter, Y=YouTube, K=TikTok. The platform prefix and code are combined internally (e.g., G + 87XVH89VV = G87XVH89VV).
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
